import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  MenuItem,
  InputAdornment,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from '@mui/material';
import { Inventory2, Edit as EditIcon } from '@mui/icons-material';
import assetService, {
  AssetCategory,
  AssetBlock,
  AssetDetail,
  AssetStatus,
  DepreciationMethod,
  ASSET_STATUSES,
  DEPRECIATION_METHODS,
} from '../../services/assetService';
import { ASSET_STATUS_LABEL, DEPRECIATION_METHOD_LABEL } from './assetFormat';

/** Shared "New Asset" / "Edit Asset" form — same fields, POST vs PUT. */

interface AssetFormState {
  name: string;
  categoryId: string;
  serialNumber: string;
  location: string;
  status: AssetStatus;
  purchaseDate: string;
  purchaseCost: string;
  grossBlock: string;
  capitalisedOn: string;
  putToUseOn: string;
  salvageValue: string;
  warrantyUntil: string;
  depreciationMethod: DepreciationMethod | '';
  usefulLifeMonths: string;
  itBlockId: string;
}

const dateOnly = (v: string | null | undefined): string => (v ? v.slice(0, 10) : '');

const emptyForm: AssetFormState = {
  name: '',
  categoryId: '',
  serialNumber: '',
  location: '',
  status: 'IN_STORE',
  purchaseDate: '',
  purchaseCost: '',
  grossBlock: '',
  capitalisedOn: '',
  putToUseOn: '',
  salvageValue: '',
  warrantyUntil: '',
  depreciationMethod: '',
  usefulLifeMonths: '',
  itBlockId: '',
};

const formFromAsset = (a: AssetDetail): AssetFormState => ({
  name: a.name,
  categoryId: a.category_id ? String(a.category_id) : '',
  serialNumber: a.serial_number || '',
  location: a.location || '',
  status: a.status,
  purchaseDate: dateOnly(a.purchase_date),
  purchaseCost: a.purchase_cost !== null && a.purchase_cost !== undefined ? String(a.purchase_cost) : '',
  grossBlock: a.gross_block !== null && a.gross_block !== undefined ? String(a.gross_block) : '',
  capitalisedOn: dateOnly(a.capitalised_on),
  putToUseOn: dateOnly(a.put_to_use_on),
  salvageValue: a.salvage_value !== null && a.salvage_value !== undefined ? String(a.salvage_value) : '',
  warrantyUntil: dateOnly(a.warranty_until),
  depreciationMethod: a.depreciation_method || '',
  usefulLifeMonths: a.useful_life_months !== null && a.useful_life_months !== undefined ? String(a.useful_life_months) : '',
  itBlockId: a.it_block_id ? String(a.it_block_id) : '',
});

interface AssetFormDialogProps {
  open: boolean;
  mode: 'create' | 'edit';
  /** Required when mode === 'edit'. */
  asset?: AssetDetail | null;
  categories: AssetCategory[];
  blocks: AssetBlock[];
  onClose: () => void;
  onSaved: () => void;
}

const AssetFormDialog: React.FC<AssetFormDialogProps> = ({ open, mode, asset, categories, blocks, onClose, onSaved }) => {
  const [form, setForm] = useState<AssetFormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm(mode === 'edit' && asset ? formFromAsset(asset) : emptyForm);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, asset?.id]);

  const set = <K extends keyof AssetFormState>(key: K, value: AssetFormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      setError('Asset name is required');
      return;
    }
    setSaving(true);
    setError(null);
    const payload = {
      name: form.name.trim(),
      categoryId: form.categoryId ? Number(form.categoryId) : undefined,
      serialNumber: form.serialNumber || undefined,
      location: form.location || undefined,
      status: form.status,
      purchaseDate: form.purchaseDate || undefined,
      purchaseCost: form.purchaseCost ? Number(form.purchaseCost) : undefined,
      grossBlock: form.grossBlock ? Number(form.grossBlock) : undefined,
      capitalisedOn: form.capitalisedOn || undefined,
      putToUseOn: form.putToUseOn || undefined,
      salvageValue: form.salvageValue ? Number(form.salvageValue) : undefined,
      warrantyUntil: form.warrantyUntil || undefined,
      depreciationMethod: form.depreciationMethod || undefined,
      usefulLifeMonths: form.usefulLifeMonths ? Number(form.usefulLifeMonths) : undefined,
      itBlockId: form.itBlockId ? Number(form.itBlockId) : undefined,
    };
    try {
      if (mode === 'edit' && asset) {
        // updateAsset has no `status` field server-side (see PATCH /:id/status
        // for that) — sending it is harmless (ignored) but omitted here for honesty.
        await assetService.updateAsset(asset.id, payload);
      } else {
        await assetService.createAsset(payload);
      }
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err.message || `Failed to ${mode === 'edit' ? 'update' : 'create'} asset`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {mode === 'edit' ? <EditIcon color="primary" /> : <Inventory2 color="primary" />}
        {mode === 'edit' ? 'Edit Asset' : 'New Fixed Asset'}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Asset Name"
            required
            size="small"
            fullWidth
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            disabled={saving}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              select
              label="Category"
              size="small"
              fullWidth
              value={form.categoryId}
              onChange={(e) => set('categoryId', e.target.value)}
              disabled={saving}
            >
              <MenuItem value="">Uncategorised</MenuItem>
              {categories.map((c) => (
                <MenuItem key={c.id} value={String(c.id)}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Status"
              size="small"
              fullWidth
              helperText={mode === 'edit' ? 'Change via the asset’s Status action instead' : undefined}
              value={form.status}
              onChange={(e) => set('status', e.target.value as AssetStatus)}
              disabled={saving || mode === 'edit'}
            >
              {ASSET_STATUSES.filter((s) => s !== 'DISPOSED').map((s) => (
                <MenuItem key={s} value={s}>
                  {ASSET_STATUS_LABEL[s]}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Serial Number"
              size="small"
              fullWidth
              value={form.serialNumber}
              onChange={(e) => set('serialNumber', e.target.value)}
              disabled={saving}
            />
            <TextField
              label="Location"
              size="small"
              fullWidth
              value={form.location}
              onChange={(e) => set('location', e.target.value)}
              disabled={saving}
            />
          </Box>

          <Divider textAlign="left">
            <Typography variant="caption" color="text.secondary">Acquisition</Typography>
          </Divider>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Purchase Date"
              type="date"
              size="small"
              fullWidth
              value={form.purchaseDate}
              onChange={(e) => set('purchaseDate', e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              disabled={saving}
            />
            <TextField
              label="Purchase Cost"
              type="number"
              size="small"
              fullWidth
              value={form.purchaseCost}
              onChange={(e) => set('purchaseCost', e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
              disabled={saving}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Gross Block"
              type="number"
              size="small"
              fullWidth
              helperText="Depreciable base; defaults to purchase cost"
              value={form.grossBlock}
              onChange={(e) => set('grossBlock', e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
              disabled={saving}
            />
            <TextField
              label="Salvage Value"
              type="number"
              size="small"
              fullWidth
              helperText="Defaults to 5% of gross block"
              value={form.salvageValue}
              onChange={(e) => set('salvageValue', e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
              disabled={saving}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Capitalised On"
              type="date"
              size="small"
              fullWidth
              value={form.capitalisedOn}
              onChange={(e) => set('capitalisedOn', e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              disabled={saving}
            />
            <TextField
              label="Put to Use On"
              type="date"
              size="small"
              fullWidth
              helperText="Required before this asset can depreciate"
              value={form.putToUseOn}
              onChange={(e) => set('putToUseOn', e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              disabled={saving}
            />
          </Box>
          <TextField
            label="Warranty Until"
            type="date"
            size="small"
            fullWidth
            value={form.warrantyUntil}
            onChange={(e) => set('warrantyUntil', e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
            disabled={saving}
          />

          <Divider textAlign="left">
            <Typography variant="caption" color="text.secondary">Depreciation</Typography>
          </Divider>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              select
              label="Method (Companies Act)"
              size="small"
              fullWidth
              helperText="Falls back to the category's method if left blank"
              value={form.depreciationMethod}
              onChange={(e) => set('depreciationMethod', e.target.value as DepreciationMethod | '')}
              disabled={saving}
            >
              <MenuItem value="">Category default</MenuItem>
              {DEPRECIATION_METHODS.map((m) => (
                <MenuItem key={m} value={m}>
                  {DEPRECIATION_METHOD_LABEL[m]}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Useful Life (months)"
              type="number"
              size="small"
              fullWidth
              value={form.usefulLifeMonths}
              onChange={(e) => set('usefulLifeMonths', e.target.value)}
              disabled={saving}
            />
          </Box>
          <TextField
            select
            label="Income Tax Block"
            size="small"
            fullWidth
            helperText="Falls back to the category's block if left blank"
            value={form.itBlockId}
            onChange={(e) => set('itBlockId', e.target.value)}
            disabled={saving}
          >
            <MenuItem value="">Category default</MenuItem>
            {blocks.map((b) => (
              <MenuItem key={b.id} value={String(b.id)}>
                {b.block_name} ({b.rate_pct}%)
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2.5 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving || !form.name.trim()}>
          {saving ? 'Saving…' : mode === 'edit' ? 'Save Changes' : 'Create Asset'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssetFormDialog;
